import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

const field = "w-full bg-transparent border border-border rounded-lg focus:border-accent outline-none px-4 py-3 text-ink transition-colors";
const lbl = "text-xs uppercase tracking-widest text-muted2 block mb-2";

function Group({ title, children }) {
  return (
    <div className="rounded-xl border border-border p-6">
      <h3 className="font-mono uppercase tracking-widest text-sm text-lime mb-5">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function ContentForm() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef();
  const editedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get("/content")
      .then((r) => { if (alive && !editedRef.current) setC(r.data); })
      .catch(() => toast.error("Failed to load content"));
    return () => { alive = false; };
  }, []);

  const set = (k, v) => { editedRef.current = true; setC((p) => ({ ...p, [k]: v })); };

  const onImg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("founder_image", data.url); toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", c); toast.success("Saved, it's live on the site"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!c) return <p className="text-muted2">Loading content…</p>;

  const F = (k, label, textarea = false) => (
    <div>
      <label className={lbl}>{label}</label>
      {textarea
        ? <textarea data-testid={`content-${k}`} rows={3} className={field} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} />
        : <input data-testid={`content-${k}`} className={field} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} />}
    </div>
  );

  return (
    <div data-testid="content-panel">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Homepage Content</h1>
        <button onClick={save} disabled={saving} data-testid="content-save"
          className="rounded-full bg-accent text-bg px-6 py-3 text-sm font-medium hover:bg-accent-hover disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Group title="Hero">
          <div className="grid grid-cols-2 gap-3">{F("hero_eyebrow_a", "Eyebrow left")}{F("hero_eyebrow_b", "Eyebrow right")}</div>
          {F("hero_line1", "Headline line 1")}
          <div className="grid grid-cols-2 gap-3">{F("hero_line2_pre", "Line 2 (text)")}{F("hero_line2_em", "Line 2 (accent word)")}</div>
          {F("hero_line3", "Headline line 3")}
          <div className="grid grid-cols-2 gap-3">{F("cta_primary", "Primary button")}{F("cta_secondary", "Secondary link")}</div>
          {F("work_heading", "Work section heading")}
        </Group>

        <Group title="Founder">
          <div className="grid grid-cols-2 gap-3">{F("founder_tag", "Tag")}{F("founder_name", "Name")}</div>
          {F("founder_role", "Role")}
          {F("founder_lead", "Lead statement", true)}
          {F("founder_body", "Body paragraph", true)}
          <div>
            <label className={lbl}>Founder image</label>
            <div className="flex items-center gap-4">
              {c.founder_image && <img src={mediaUrl(c.founder_image)} alt="" className="w-16 h-20 object-cover rounded border border-border" />}
              <input type="text" className={field} value={c.founder_image || ""} onChange={(e) => set("founder_image", e.target.value)} placeholder="URL or upload" />
              <input ref={imgRef} type="file" accept="image/*" hidden onChange={onImg} data-testid="content-founder-image-upload" />
              <button onClick={() => imgRef.current.click()} className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-border px-4 py-3 text-sm hover:border-accent"><Upload size={16} /> Upload</button>
            </div>
            {uploading && <span className="text-muted2 text-xs mt-2 inline-block">Uploading…</span>}
          </div>
        </Group>

        <Group title="Closing line">
          {F("closing", "Closing manifesto", true)}
        </Group>

        <Group title="About page">
          {F("about_heading_pre", "Heading line 1")}
          <div className="grid grid-cols-2 gap-3">{F("about_heading_em", "Accent word")}{F("about_heading_post", "After accent")}</div>
          {F("about_sub", "Sub-headline", true)}
          {F("about_founder_bio", "Founder bio (About page)", true)}
          {F("about_closing", "Closing line")}
        </Group>

        <Group title="Footer">
          {F("footer_tagline", "Tagline")}
          {F("footer_blurb", "Blurb", true)}
          {F("footer_email", "Contact email")}
          <div className="grid grid-cols-3 gap-3">{F("footer_linkedin", "LinkedIn")}{F("footer_instagram", "Instagram")}{F("footer_behance", "Behance")}</div>
          {F("footer_note", "Bottom note")}
        </Group>
      </div>
    </div>
  );
}
